import { CAMERA, PLANET, PLAYER, WORLD_RULES } from '../domain/content.js';
import {
  INPUT_JUMP,
  INPUT_LEFT,
  INPUT_RIGHT,
  INPUT_TOGGLE_CAMERA,
  INPUT_ZOOM_IN,
  INPUT_ZOOM_OUT,
  type GameState,
  type Input,
} from '../contract.js';

export function step(state: GameState, input: Input, dtSec: number): GameState {
  state.tick += 1;
  updateStatusTimers(state);
  updateCameraMode(state, input.pressedMask);
  updateCameraZoom(state, input.pressedMask);
  updateJumpBuffer(state, input.pressedMask);
  updateTangentMotion(state, input.mask, dtSec);
  updateJumpAndGravity(state, input.mask, dtSec);
  updateAngle(state, dtSec);
  updateWorldInteractions(state, dtSec);
  updateCameraPosition(state);
  updatePoseTimers(state);

  return state;
}

function updateCameraMode(state: GameState, pressedMask: number): void {
  if ((pressedMask & INPUT_TOGGLE_CAMERA) === 0) return;
  state.cameraMode = state.cameraMode === 'locked' ? 'deadzone' : 'locked';
  state.cameraAngle = state.player.angle;
}

function updateCameraZoom(state: GameState, pressedMask: number): void {
  if ((pressedMask & INPUT_ZOOM_IN) !== 0) state.cameraZoom += CAMERA.zoomStep;
  if ((pressedMask & INPUT_ZOOM_OUT) !== 0) state.cameraZoom -= CAMERA.zoomStep;
  state.cameraZoom = clamp(state.cameraZoom, CAMERA.minZoom, CAMERA.maxZoom);
}

function updateCameraPosition(state: GameState): void {
  if (state.cameraMode === 'locked') {
    state.cameraAngle = state.player.angle;
    return;
  }

  const radius = PLANET.radius + state.player.altitude;
  const halfAngle = (CAMERA.deadzoneHalfWidthPx / state.cameraZoom) / radius;
  const delta = shortestAngle(state.player.angle - state.cameraAngle);
  if (Math.abs(delta) <= halfAngle) return;
  state.cameraAngle = wrapAngle(state.player.angle - Math.sign(delta) * halfAngle);
}

function updateJumpBuffer(state: GameState, pressedMask: number): void {
  if ((pressedMask & INPUT_JUMP) !== 0) state.player.jumpBufferTicks = PLAYER.jumpBufferTicks;
  else if (state.player.jumpBufferTicks > 0) state.player.jumpBufferTicks -= 1;
}

function updateTangentMotion(state: GameState, mask: number, dtSec: number): void {
  let direction = 0;
  if ((mask & INPUT_LEFT) !== 0) direction -= 1;
  if ((mask & INPUT_RIGHT) !== 0) direction += 1;

  const player = state.player;
  if (direction !== 0) {
    const acceleration = state.inWater ? PLAYER.airAcceleration * 0.52 : player.grounded ? PLAYER.runAcceleration : PLAYER.airAcceleration;
    const maxSpeed = state.inWater ? PLAYER.maxSwimSpeed : player.grounded ? PLAYER.maxGroundSpeed : PLAYER.maxAirSpeed;
    player.tangentVelocity = clamp(player.tangentVelocity + direction * acceleration * dtSec, -maxSpeed, maxSpeed);
    player.facing = direction < 0 ? -1 : 1;
    return;
  }

  const damping = player.grounded ? PLAYER.groundFriction : PLAYER.airDrag;
  const drag = Math.max(0, 1 - damping * dtSec);
  player.tangentVelocity *= drag;
  if (Math.abs(player.tangentVelocity) < 1.5) player.tangentVelocity = 0;
}

function updateJumpAndGravity(state: GameState, mask: number, dtSec: number): void {
  const player = state.player;
  if (state.inWater) {
    player.grounded = false;
    player.altitude = 0;
    player.radialVelocity = 0;
    player.coyoteTicks = 0;
    player.jumpBufferTicks = 0;
    return;
  }

  if (player.grounded) player.coyoteTicks = PLAYER.coyoteTicks;
  else if (player.coyoteTicks > 0) player.coyoteTicks -= 1;

  if (player.jumpBufferTicks > 0 && player.coyoteTicks > 0) {
    player.radialVelocity = PLAYER.jumpVelocity;
    player.grounded = false;
    player.coyoteTicks = 0;
    player.jumpBufferTicks = 0;
    player.landingTicks = 0;
  }

  const jumpHeld = (mask & INPUT_JUMP) !== 0;
  const gravityScale = player.radialVelocity > 0 && jumpHeld
    ? PLAYER.risingHoldGravityScale
    : player.radialVelocity < 0
      ? PLAYER.fallingGravityScale
      : 1;

  player.radialVelocity = Math.max(player.radialVelocity - PLAYER.gravity * gravityScale * dtSec, -PLAYER.maxFallSpeed);
  player.altitude += player.radialVelocity * dtSec;

  if (player.altitude <= 0) {
    const landed = !player.grounded && player.radialVelocity < -80;
    player.altitude = 0;
    player.radialVelocity = 0;
    player.grounded = true;
    if (landed) player.landingTicks = PLAYER.landingSquashTicks;
  } else {
    player.grounded = false;
  }
}

function updateAngle(state: GameState, dtSec: number): void {
  const radius = PLANET.radius + state.player.altitude;
  state.player.angle = wrapAngle(state.player.angle + (state.player.tangentVelocity / radius) * dtSec);
  state.playerSurfaceDistance = surfaceDistanceFromAngle(state.player.angle);
  state.player.walkCycle = wrapCycle(
    state.player.walkCycle + Math.abs(state.player.tangentVelocity) * PLAYER.walkCyclePerPixel * dtSec,
  );
}

function updateWorldInteractions(state: GameState, dtSec: number): void {
  const distance = state.playerSurfaceDistance;
  const overWater = distance <= WORLD_RULES.waterStartDistance && distance >= WORLD_RULES.waterEndDistance;
  const inWater = overWater && (state.inWater || state.player.altitude <= 0);
  state.inWater = inWater;

  if (inWater) {
    state.swimStamina = Math.max(0, state.swimStamina - WORLD_RULES.swimStaminaDrainPerSecond * dtSec);
    state.player.grounded = false;
    state.player.altitude = 0;
    state.player.radialVelocity = 0;
    setStatus(state, 'water', 18);
    if (state.swimStamina <= 0) returnHome(state);
  } else {
    state.swimStamina = Math.min(WORLD_RULES.swimStaminaMax, state.swimStamina + WORLD_RULES.swimStaminaRecoverPerSecond * dtSec);
  }

  if (state.playerSurfaceDistance >= WORLD_RULES.forestStartDistance) {
    setSurfaceDistance(state, WORLD_RULES.forestStartDistance);
    state.player.tangentVelocity = Math.min(0, state.player.tangentVelocity);
    state.player.blockedTicks = WORLD_RULES.blockedTicks;
    setStatus(state, 'forest-blocked', 80);
  }

  const leftDistance = Math.abs(state.playerSurfaceDistance - WORLD_RULES.leftDiscoveryDistance);
  if (!state.discoveries.leftActionSeen && leftDistance <= WORLD_RULES.actionRadius) {
    state.discoveries.leftActionSeen = true;
    setStatus(state, 'left-action', 180);
  }

  const rightDistance = Math.abs(state.playerSurfaceDistance - WORLD_RULES.rightDiscoveryDistance);
  if (!state.discoveries.rightActionSeen && rightDistance <= WORLD_RULES.actionRadius) {
    state.discoveries.rightActionSeen = true;
    state.minimapUnlocked = true;
    setStatus(state, 'minimap-unlocked', 210);
  }
}

function updatePoseTimers(state: GameState): void {
  if (state.player.landingTicks > 0) state.player.landingTicks -= 1;
  if (state.player.blockedTicks > 0) state.player.blockedTicks -= 1;
  if (Math.abs(state.player.tangentVelocity) < 8 && state.player.grounded && !state.inWater) state.player.idleTicks += 1;
  else state.player.idleTicks = 0;
}

function updateStatusTimers(state: GameState): void {
  if (state.statusTicks > 0) {
    state.statusTicks -= 1;
    if (state.statusTicks <= 0) {
      state.statusTicks = 0;
      state.statusMessage = 'none';
    }
  }
  if (state.homeReturnTicks > 0) state.homeReturnTicks -= 1;
}

function setStatus(state: GameState, kind: GameState['statusMessage'], ticks: number): void {
  if (state.statusMessage === kind && state.statusTicks > ticks) return;
  state.statusMessage = kind;
  state.statusTicks = ticks;
}

function returnHome(state: GameState): void {
  setSurfaceDistance(state, WORLD_RULES.homeSurfaceDistance);
  state.player.altitude = 0;
  state.player.tangentVelocity = 0;
  state.player.radialVelocity = 0;
  state.player.grounded = true;
  state.player.coyoteTicks = 0;
  state.player.jumpBufferTicks = 0;
  state.player.landingTicks = 0;
  state.player.blockedTicks = 0;
  state.inWater = false;
  state.swimStamina = WORLD_RULES.swimStaminaMax;
  state.homeReturnTicks = WORLD_RULES.homeReturnTicks;
  state.cameraAngle = state.player.angle;
  setStatus(state, 'returned-home', WORLD_RULES.homeReturnTicks);
}

function setSurfaceDistance(state: GameState, distance: number): void {
  state.playerSurfaceDistance = distance;
  state.player.angle = wrapAngle(PLANET.startAngle + distance / PLANET.radius);
}

function surfaceDistanceFromAngle(angle: number): number {
  return shortestAngle(angle - PLANET.startAngle) * PLANET.radius;
}

function wrapAngle(value: number): number {
  let angle = value;
  while (angle <= -Math.PI) angle += Math.PI * 2;
  while (angle > Math.PI) angle -= Math.PI * 2;
  return angle;
}

function shortestAngle(value: number): number {
  return wrapAngle(value);
}

function wrapCycle(value: number): number {
  return value - Math.floor(value);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
